import { createContext, useContext, useEffect, useState } from 'react';
import en from './en.json';
import sv from './sv.json';
import nl from './nl.json';

export const languages = {
  en: { label: 'English', host: 'amberbytes.org' },
  sv: { label: 'Svenska', host: 's.amberbytes.org' },
  nl: { label: 'Nederlands', host: 'n.amberbytes.org' },
};

const translations = { en, sv, nl };

function detectLanguage() {
  if (typeof window === 'undefined') return 'en';
  const host = window.location.hostname;
  if (host === 's.amberbytes.org') return 'sv';
  if (host === 'n.amberbytes.org') return 'nl';
  return 'en';
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => detectLanguage());

  useEffect(() => {
    const detected = detectLanguage();
    setLang(detected);
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    const t = translations[lang];
    if (t?.meta?.title) {
      document.title = t.meta.title;
    }
    if (t?.meta?.description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', t.meta.description);
    }
  }, [lang]);

  const t = (key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        return fallback || key;
      }
    }
    return typeof value === 'string' ? value : fallback || key;
  };

  const switchLanguage = (nextLang) => {
    if (nextLang === lang) return;
    const target = languages[nextLang];
    if (!target) return;
    const protocol = window.location.protocol;
    const pathname = window.location.pathname;
    const search = window.location.search;
    const url = `${protocol}//${target.host}${pathname}${search}`;
    window.location.href = url;
  };

  return (
    <I18nContext.Provider value={{ lang, t, switchLanguage }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}

export { translations };
